// src/cockpit4/components/DataBigscreenPanel.tsx
// ============================================
// 品牌监测 → 数据大盘（截图 1 功能）
//
// 结构：
//   · 顶部过滤条：+ 增加品牌监测 / 平台下拉 / 问题下拉 / 日期范围
//   · KPI 条：品牌得分 / 对话次数 / 提及对话次数 / 提及率 / 品牌提及次数 / SOV
//            / Top1提及率 / Top3提及率 / 平均提及排名 / 品牌提及好感度
//   · 平台表现表：8 平台 × 网页/手机 × 8 指标（SOV/对话/提及率/Top1/Top3/Top10/平均排名/好感度）
//   · 提及曝光：品牌得分趋势折线图 + 平均提及排名柱状图
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Plus, ChevronDown, Calendar, Table2, LayoutGrid,
  Info, TrendingUp, Award, MessageCircle, Volume2, Star,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, Legend,
} from "recharts";
import type { BrandMonitorData } from "../../runtime/GrowthFactory/brandMonitorData";
import {
  ALL_MONITOR_PLATFORMS,
} from "../../runtime/GrowthFactory/brandMonitorData";

interface Props {
  data: BrandMonitorData;
  brand: string;
  onBrandChange: (v: string) => void;
  onAddBrand: () => void;
  platforms: string[];
  onPlatformsChange: (keys: string[]) => void;
  questions: { id: string; label: string }[];
  questionsFilter: string[];
  onQuestionsChange: (ids: string[]) => void;
  dateStart: string;
  dateEnd: string;
  onDateChange: (start: string, end: string) => void;
}

const TABLE_ROWS = [
  { key: "sov", label: "SOV", suffix: "", k: "sov", fmt: (v: number) => v === 0 ? "未提及" : `${Math.round(v * 100)}%` },
  { key: "dialogs", label: "对话次数", k: "dialogs", fmt: (v: number) => v === 0 ? "未提及" : `${v}` },
  { key: "mentionRate", label: "提及率", k: "mentionRate", fmt: (v: number) => v === 0 ? "未提及" : `${Math.round(v * 100)}%` },
  { key: "top1Rate", label: "Top1(首位)提及率", k: "top1Rate", fmt: (v: number) => v === 0 ? "未提及" : `${Math.round(v * 100)}%` },
  { key: "top3Rate", label: "Top3(首屏)提及率", k: "top3Rate", fmt: (v: number) => v === 0 ? "未提及" : `${Math.round(v * 100)}%` },
  { key: "top10Rate", label: "Top10提及率", k: "top10Rate", fmt: (v: number) => v === 0 ? "未提及" : `${Math.round(v * 100)}%` },
  { key: "avgRank", label: "平均提及排名", k: "avgRank", fmt: (v: number) => v < 0 ? "未提及" : `${v.toFixed(1)}` },
  { key: "mentionFavor", label: "提及好感度", k: "mentionFavor", fmt: (v: number) => v < 0 ? "--" : `${Math.round(v)}` },
] as const;

/** 格式化 KPI 值 */
function fmtKPI(k: keyof BrandMonitorData["kpi"], v: number) {
  if (["mentionRate", "sov", "top1Rate", "top3Rate",
    "sentimentPositive", "sentimentNeutral", "sentimentNegative"].includes(k)) {
    return `${Math.round(v * 100)}%`;
  }
  if (k === "mentionFavor" || k === "avgRank") {
    return v < 0 ? "--" : `${Math.round(v)}`;
  }
  return `${Math.round(v)}`;
}

function KPIHelp({ tip }: { tip: string }) {
  return (
    <span className="text-[9px] text-gray-500 inline-flex items-center gap-0.5 align-middle ml-1 cursor-help">
      <Info size={9} /> <span>{tip}</span>
    </span>
  );
}

export default function DataBigscreenPanel(props: Props) {
  const {
    data, brand, onBrandChange, onAddBrand,
    platforms, onPlatformsChange,
    questions, questionsFilter, onQuestionsChange,
    dateStart, dateEnd, onDateChange,
  } = props;
  const [view, setView] = useState<"table" | "card">("table");
  const [pOpen, setPOpen] = useState(false);
  const [qOpen, setQOpen] = useState(false);
  const kpi = data.kpi;

  const rankRadar = useMemo(() => {
    const baseKPI = {
      品牌得分: kpi.brandScore,
      提及率: Math.round(kpi.mentionRate * 100),
      Top3率: Math.round(kpi.top3Rate * 100),
      SOV: Math.round(kpi.sov * 100),
      好感度: kpi.mentionFavor < 0 ? 50 : Math.round(kpi.mentionFavor),
    };
    return data.rankRadar.map((r) => r);
  }, [data.rankRadar, kpi]);

  return (
    <div className="flex flex-col gap-3 p-4">
      {/* 过滤工具栏 */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <LayoutDashboard size={15} className="text-violet-400" />
          <span className="text-[13px] font-semibold text-white">数据大盘</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* + 增加品牌监测 */}
          <button
            onClick={onAddBrand}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px]
                       bg-white/[0.03] border border-white/[0.08] text-gray-400
                       hover:text-white hover:border-violet-500/40 transition-colors"
          >
            <Plus size={12} /> 增加品牌监测
          </button>
          <select
            value={brand}
            onChange={(e) => onBrandChange(e.target.value)}
            className="px-2.5 py-1.5 rounded-md text-[11px] bg-black/30 border border-white/[0.08] text-white focus:outline-none"
          >
            <option value={brand}>{brand}（当前）</option>
            <option value="小舍科技">小舍科技</option>
            <option value="XEAOS">XEAOS</option>
            <option value="小舍AI">小舍AI</option>
          </select>
          <div className="relative">
            <button
              onClick={() => { setPOpen((o) => !o); setQOpen(false); }}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] bg-black/30 border border-white/[0.08] text-white"
            >
              {`全部${platforms.length}个平台`} <ChevronDown size={12} />
            </button>
            {pOpen && (
              <div className="absolute top-full mt-1 left-0 z-20 bg-[#0e1220] border border-white/[0.1] rounded-md p-2 w-48 shadow-2xl">
                <div className="flex flex-wrap gap-1">
                  {ALL_MONITOR_PLATFORMS.map((p) => {
                    const on = platforms.includes(p.key);
                    return (
                      <button key={p.key}
                        onClick={() => onPlatformsChange(on ? platforms.filter((x) => x !== p.key) : [...platforms, p.key])}
                        className={`px-2 py-1 text-[10px] rounded ${on ? "text-white bg-white/10 border border-white/10" : "text-gray-500 border border-transparent"}`}
                      >
                        <span className="w-2 h-2 inline-block rounded-full mr-1" style={{ background: p.color }} />
                        {p.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="relative">
            <button
              onClick={() => { setQOpen((o) => !o); setPOpen(false); }}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] bg-black/30 border border-white/[0.08] text-white"
            >
              {`全部${questionsFilter.length}个问题`} <ChevronDown size={12} />
            </button>
            {qOpen && (
              <div className="absolute top-full mt-1 left-0 z-20 bg-[#0e1220] border border-white/[0.1] rounded-md p-2 w-80 shadow-2xl max-h-60 overflow-auto">
                {questions.map((q) => {
                  const on = questionsFilter.includes(q.id);
                  return (
                    <button key={q.id}
                      onClick={() => onQuestionsChange(on ? questionsFilter.filter((x) => x !== q.id) : [...questionsFilter, q.id])}
                      className={`block w-full text-left px-2 py-1 rounded text-[10px] ${on ? "text-white bg-white/10" : "text-gray-400"}`}
                    >
                      ▸ {q.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 px-2 py-1.5 rounded-md text-[11px] bg-black/30 border border-white/[0.08] text-gray-300">
            <Calendar size={12} />
            <input type="date" value={dateStart} onChange={(e) => onDateChange(e.target.value, dateEnd)}
              className="bg-transparent outline-none w-[88px] text-white" />
            <span className="text-gray-500">至</span>
            <input type="date" value={dateEnd} onChange={(e) => onDateChange(dateStart, e.target.value)}
              className="bg-transparent outline-none w-[88px] text-white" />
          </div>
        </div>
      </div>

      {/* KPI 行 */}
      <div className="grid grid-cols-2 md:grid-cols-5 xl:grid-cols-10 gap-2">
        <div className="rounded-lg bg-white/[0.02] border border-violet-500/20 p-2.5 col-span-1">
          <div className="text-[20px] font-bold text-violet-300 tracking-tight">
            {kpi.brandScore}<small className="text-[10px] text-gray-500 font-mono ml-0.5">/100</small>
          </div>
          <div className="text-[10px] text-gray-300 mt-1 flex items-center gap-1">
            <Award size={11} className="text-violet-400" /> 品牌得分
            <KPIHelp tip="提及率+Top3率+排名+好感度" />
          </div>
        </div>
        {[
          { k: "dialogs", label: "对话次数", Icon: MessageCircle, color: "text-cyan-400" },
          { k: "mentionDialogs", label: "提及对话次数", Icon: TrendingUp, color: "text-emerald-400" },
          { k: "mentionRate", label: "提及率", Icon: Volume2, color: "text-blue-400" },
          { k: "mentions", label: "品牌提及次数", Icon: Star, color: "text-amber-400" },
          { k: "sov", label: "SOV", Icon: LayoutGrid, color: "text-pink-400" },
          { k: "top1Rate", label: "Top1(首位)提及率", Icon: Award, color: "text-emerald-400" },
          { k: "top3Rate", label: "Top3(首屏)提及率", Icon: TrendingUp, color: "text-cyan-400" },
          { k: "avgRank", label: "平均提及排名", Icon: Award, color: "text-violet-400" },
          { k: "mentionFavor", label: "品牌提及好感度", Icon: Star, color: "text-pink-400" },
        ].map((item) => (
          <div key={item.k} className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-2.5">
            <div className={`text-[18px] font-bold tracking-tight ${item.color}`}>
              {fmtKPI(item.k as any, (kpi as any)[item.k])}
            </div>
            <div className="text-[10px] text-gray-300 mt-1 flex items-center gap-1">
              <item.Icon size={11} className={item.color} /> {item.label}
            </div>
          </div>
        ))}
      </div>

      {/* 平台表现 */}
      <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="w-1 h-4 rounded bg-violet-500" />
            <span className="text-[12px] font-semibold text-white">平台表现</span>
          </div>
          <div className="flex items-center gap-1 p-0.5 rounded-md bg-black/30 border border-white/[0.06]">
            <button
              onClick={() => setView("table")}
              className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] ${view === "table" ? "bg-violet-500/20 text-violet-300 border border-violet-500/30" : "text-gray-500"}`}
            >
              <Table2 size={11} /> 表格视图
            </button>
            <button
              onClick={() => setView("card")}
              className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] ${view === "card" ? "bg-violet-500/20 text-violet-300 border border-violet-500/30" : "text-gray-500"}`}
            >
              <LayoutGrid size={11} /> 卡片视图
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-[10.5px] border-collapse">
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th className="text-left text-gray-500 font-mono font-normal p-2 min-w-[130px] sticky left-0 bg-[#0c1020]">
                  <div className="flex flex-col items-start">
                    <span className="text-gray-400 mb-1">指标</span>
                    <span className="text-gray-500">平台</span>
                  </div>
                </th>
                {ALL_MONITOR_PLATFORMS.map((p) => (
                  <th key={p.key} colSpan={p.endpoints.length} className="p-2 min-w-[120px]">
                    <div className="flex items-center justify-center gap-1.5 mb-1">
                      <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white" style={{ background: p.color }}>
                        {p.short}
                      </span>
                      <span className="text-white">{p.name}</span>
                      {!p.enabled && <span className="text-[8px] px-1 rounded bg-white/[0.05] text-gray-500">接入中</span>}
                    </div>
                    <div className="flex gap-2 justify-center">
                      {p.endpoints.map((e) => (
                        <span key={e} className="text-[9px] font-mono text-gray-500">
                          {e === "web" ? "网页" : "手机"}
                        </span>
                      ))}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TABLE_ROWS.map((row, idx) => (
                <tr key={row.key} className={idx % 2 ? "bg-white/[0.01]" : ""}>
                  <td className="p-2 text-gray-300 font-medium sticky left-0 bg-[#0c1020] border-r border-white/[0.04]">
                    {row.label}
                    {(row.key === "sov" || row.key === "mentionRate" || row.key === "mentionFavor" || row.key === "top1Rate") && (
                      <Info size={9} className="text-gray-600 inline ml-1" />
                    )}
                  </td>
                  {ALL_MONITOR_PLATFORMS.map((p) =>
                    p.endpoints.map((end) => {
                      const metric = data.platformMetrics[p.key]?.[end];
                      if (!metric) return <td key={`${p.key}-${end}`} className="p-2 text-center text-gray-600">--</td>;
                      const raw = (metric as any)[row.k];
                      const shown = row.fmt(raw);
                      const good = (() => {
                        if (shown === "未提及" || shown === "--") return 0;
                        if (row.key === "avgRank") return raw > 0 && raw <= 3 ? 1 : 0;
                        if (["mentionFavor"].includes(row.key)) return raw >= 70 ? 1 : raw < 40 ? -1 : 0;
                        return raw >= 0.1 ? 1 : raw <= 0 ? -1 : 0;
                      })();
                      return (
                        <td key={`${p.key}-${end}`} className={`p-2 text-center ${good === 1 ? "text-emerald-400" : good === -1 ? "text-gray-500" : "text-gray-300"}`}>
                          {shown}
                        </td>
                      );
                    })
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 提及曝光：品牌得分趋势 + 平均提及排名 Top20 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-semibold text-white flex items-center gap-1.5">
              <TrendingUp size={12} className="text-emerald-400" /> 品牌得分
              <Info size={9} className="text-gray-600" />
            </span>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.brandScoreChart} margin={{ left: 5, right: 20, top: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" />
                <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" domain={[0, 100]} />
                <Tooltip contentStyle={{ background: "#0e1220", border: "1px solid rgba(255,255,255,0.08)", fontSize: 11 }} />
                <Line type="monotone" dataKey="score" stroke="#8B5CF6" strokeWidth={2.2}
                  dot={{ r: 3, fill: "#8B5CF6" }} activeDot={{ r: 5 }} name="品牌得分" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-semibold text-white flex items-center gap-1.5">
              <Award size={12} className="text-amber-400" /> 平均提及排名
            </span>
            <select className="text-[10px] bg-black/30 border border-white/[0.08] text-gray-300 rounded px-2 py-1 outline-none"
              defaultValue="top">
              <option value="top">提及率Top20品牌</option>
              <option value="self">当前品牌对比</option>
            </select>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rankRadar.slice(0, 10)} margin={{ left: 5, right: 20, top: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="brand" tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" />
                <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" />
                <Tooltip contentStyle={{ background: "#0e1220", border: "1px solid rgba(255,255,255,0.08)", fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="value" fill="#22D3EE" name="提及率表现（%）" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
